"use client";
import { cn } from "@/lib/utils";
import { FormField } from "@/schemas/type";
import { isInteger } from "lodash-es";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useRef, useState } from "react";

interface Props {
  className?: string;
  field?: FormField;
  max?: number;
  min?: number;
  onMax?: () => void;
  onMin?: () => void;
  stepper?: boolean;
}

export default function InputNumber({
  field,
  className,
  max,
  min,
  onMax,
  onMin,
  stepper = true,
}: Props) {
  const [inputValue, setInputValue] = useState(field?.value);

  const ref = useRef<HTMLInputElement>(null);

  const handleIncrement = () => {
    let newValue = parseInt(inputValue ?? "0", 10) + 1;

    if (max) {
      newValue = Math.min(newValue, max);

      if (newValue === max) {
        onMax?.();
      }
    }

    setInputValue(newValue.toString());
    field?.onChange?.(newValue.toString());
  };

  const handleDecrement = () => {
    let newValue = parseInt(inputValue ?? "0", 10) - 1;

    if (min) {
      newValue = Math.max(newValue, min);

      if (newValue === min) {
        onMin?.();
      }
    }

    setInputValue(newValue.toString());
    field?.onChange?.(newValue.toString());
  };

  return (
    <div className={cn("relative h-9", className)}>
      <input
        ref={ref}
        className="h-full w-full rounded border border-[#D9D9D9] px-2 outline-none transition-colors duration-300 focus:border-primary disabled:cursor-not-allowed disabled:bg-[#F7F7F7] disabled:text-[#AEAEAE]"
        autoComplete="off"
        step="1"
        value={inputValue}
        onChange={(e) => {
          let newValue = e.target.value;

          if (/^\d+$/.test(newValue)) {
            if (typeof min === "number" && Number(newValue) < min) {
              newValue = String(min);
            } else if (typeof max === "number" && Number(newValue) > max) {
              newValue = String(max);
            }
            setInputValue(newValue);

            field?.onChange?.(newValue);
          } else {
            setInputValue("");
            field?.onChange?.("");
          }
        }}
      />

      {stepper && (
        <div className="absolute right-0 top-0 h-full w-8 border-l border-[#D9D9D9]">
          <span
            className="flex h-1/2 w-full cursor-pointer items-center justify-center border-b border-[#D9D9D9] text-black text-opacity-50 transition-colors duration-300 hover:text-primary hover:text-opacity-100"
            onClick={handleIncrement}>
            <ChevronUp size={14} />
          </span>
          <span
            className="flex h-1/2 w-full cursor-pointer items-center justify-center text-black text-opacity-50 transition-colors duration-300 hover:text-primary hover:text-opacity-100"
            onClick={handleDecrement}>
            <ChevronDown size={14} />
          </span>
        </div>
      )}
    </div>
  );
}
